import React from "react";
import {
  Badge,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useFieldArray, useWatch } from "react-hook-form";
import { BsPlus } from "react-icons/bs";
import SectionSettingsDrawer from "./SectionSettingsDrawer";
import TemplateSectionPanel from "./TemplateSectionPanel";
import {
  createExcelDefaultSections,
  createTemplateItem,
  toBackendItemSource,
  toUiSource,
} from "./templateSectionUtils";

const newSection = (sequence) => ({
  sectionId: `SECTION_${Date.now()}`,
  sequence,
  title: "New Section",
  code: `SECTION_${sequence}`,
  description: "",
  type: "CUSTOM",
  sectionType: "CUSTOM",
  weightage: 0,
  calculationMethod: "AVERAGE_RATING",
  uiItemSource: "STATIC_TEMPLATE_ITEMS",
  itemSource: "MANUAL",
  isRequired: true,
  mandatory: true,
  allowEmployeeRating: true,
  allowManagerRating: true,
  allowComments: true,
  commentRequired: false,
  evidenceRequired: false,
  minimumItemsRequired: 1,
  items: [createTemplateItem({ sequence: 1 })],
});

const prepareSectionForForm = (section, index) => {
  const uiItemSource = toUiSource(section);
  return {
    ...section,
    sequence: index + 1,
    sectionType: section.sectionType || section.type,
    uiItemSource,
    itemSource: toBackendItemSource(uiItemSource),
    items: (section.items || []).map((item, itemIndex) => ({
      ...item,
      itemId: item.itemId || createTemplateItem().itemId,
      sequence: itemIndex + 1,
      sourceType: uiItemSource,
      commentRequired: item.commentRequired ?? (item.commentRule === "REQUIRED"),
      evidenceRequired: item.evidenceRequired ?? (item.evidenceRule === "REQUIRED"),
    })),
  };
};

const TemplateSectionsEditor = ({ control, register, setValue, formState, itemWeightageMode }) => {
  const [editingIndex, setEditingIndex] = React.useState(null);
  const settingsDisclosure = useDisclosure();
  const { fields, append, remove, move, update, replace, insert } = useFieldArray({
    control,
    name: "sections",
    keyName: "fieldKey",
  });
  const sections = useWatch({ control, name: "sections" }) || [];
  const totalWeightage = sections.reduce((sum, section) => sum + Number(section.weightage || 0), 0);
  const editingSection = editingIndex === null ? null : sections[editingIndex];

  const syncSequences = React.useCallback(() => {
    sections.forEach((section, index) => {
      if (section.sequence !== index + 1) {
        setValue(`sections.${index}.sequence`, index + 1, { shouldDirty: true });
      }
    });
  }, [sections, setValue]);

  React.useEffect(() => {
    syncSequences();
  }, [fields.length, syncSequences]);

  const openSettings = (index) => {
    setEditingIndex(index);
    settingsDisclosure.onOpen();
  };

  const handleSettingsChange = (section) => {
    if (editingIndex === null) return;
    update(editingIndex, prepareSectionForForm(section, editingIndex));
  };

  const duplicateSection = (index) => {
    const source = sections[index];
    insert(index + 1, prepareSectionForForm({
      ...source,
      sectionId: `${source.sectionId || source.code}_COPY_${Date.now()}`,
      code: `${source.code || "SECTION"}_COPY`.slice(0, 50),
      title: `${source.title || "Section"} Copy`,
      items: (source.items || []).map((item) => ({
        ...item,
        itemId: createTemplateItem().itemId,
      })),
    }, index + 1));
  };

  const resetToExcelDefault = () => {
    replace(createExcelDefaultSections().map(prepareSectionForForm));
    setValue("itemWeightageMode", "ABSOLUTE_SECTION_WEIGHT", { shouldDirty: true });
  };

  return (
    <VStack align="stretch" spacing={4} minW={0}>
      <Flex justify="space-between" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} gap={3}>
        <HStack wrap="wrap">
          <Text fontWeight="semibold">Sections and weightage</Text>
          <Badge colorScheme={totalWeightage === 100 ? "green" : "orange"}>{totalWeightage}% total</Badge>
          {formState.isDirty ? <Badge colorScheme="purple">Pending save</Badge> : null}
        </HStack>
        <HStack wrap="wrap" width={{ base: "100%", md: "auto" }} minW={0}>
          <Button size="sm" variant="outline" onClick={resetToExcelDefault} width={{ base: "100%", sm: "auto" }}>Use Excel structure</Button>
          <Button size="sm" variant="outline" leftIcon={<BsPlus />} onClick={() => append(newSection(sections.length + 1))} width={{ base: "100%", sm: "auto" }}>
            Add section
          </Button>
        </HStack>
      </Flex>

      {fields.map((field, index) => (
        <TemplateSectionPanel
          key={field.fieldKey}
          control={control}
          register={register}
          setValue={setValue}
          section={sections[index] || field}
          sectionIndex={index}
          itemWeightageMode={itemWeightageMode}
          isDirty={formState.isDirty}
          onEditSettings={() => openSettings(index)}
          onDuplicate={() => duplicateSection(index)}
          onMoveUp={() => index > 0 && move(index, index - 1)}
          onMoveDown={() => index < fields.length - 1 && move(index, index + 1)}
          onDelete={() => remove(index)}
        />
      ))}

      <SectionSettingsDrawer
        isOpen={settingsDisclosure.isOpen}
        onClose={settingsDisclosure.onClose}
        section={editingSection}
        onChange={handleSettingsChange}
      />
    </VStack>
  );
};

export default TemplateSectionsEditor;
